import { useEffect, useState } from "react";
import { api, money, STATUS_COLORS } from "@/hub/lib/api";
import { CalendarDays, Users, TrendingUp, MapPin, Mountain, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { useNavigate } from "react-router-dom";

const Metric = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white border border-border p-5 flex flex-col justify-between" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-400">{label}</span>
      <Icon className={`h-4 w-4 ${accent ? "text-terracotta" : "text-forest"}`} strokeWidth={1.6} />
    </div>
    <div className="text-4xl font-light tracking-tighter mt-4 font-mono text-stone-900">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.dashboard().then(setData);
  }, []);

  if (!data) return <div className="text-stone-400">Loading operations…</div>;

  const pipelineData = data.pipeline.filter((p) => p.status !== "Lost");
  const pipeColors = ["#A8A29E", "#3B82F6", "#F59E0B", "#10B981", "#1C3F35"];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Client Groups" value={data.total_groups} icon={Users} />
        <Metric label="Confirmed Events" value={data.confirmed_events} icon={CalendarDays} />
        <Metric label="Confirmed Revenue" value={money(data.revenue)} icon={TrendingUp} accent />
        <Metric label="Zone Reservations" value={data.total_reservations} icon={MapPin} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border p-6">
          <h3 className="font-heading text-lg font-medium mb-1">Lead Pipeline</h3>
          <p className="text-xs text-stone-400 mb-4">Relationship management funnel across all groups</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pipelineData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="status" tick={{ fontSize: 12, fill: "#78716C" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#78716C" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#F5F5F4" }} contentStyle={{ borderRadius: 4, border: "1px solid #E7E5E4", fontSize: 12 }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {pipelineData.map((_, i) => (
                  <Cell key={i} fill={pipeColors[i % pipeColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border p-6">
          <h3 className="font-heading text-lg font-medium mb-1 flex items-center gap-2"><Mountain className="h-4 w-4 text-forest" /> Zone Utilization</h3>
          <p className="text-xs text-stone-400 mb-4">Total reservations per zone</p>
          <div className="space-y-3">
            {data.zone_utilization.map((z) => {
              const max = Math.max(...data.zone_utilization.map((x) => x.count), 1);
              return (
                <div key={z.name} data-testid={`zone-util-${z.name.replace(/\s/g, "-")}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-stone-600">{z.name}</span>
                    <span className="font-mono text-stone-400">{z.count}</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-sm overflow-hidden">
                    <div className="h-full rounded-sm transition-all" style={{ width: `${(z.count / max) * 100}%`, background: z.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white border border-border">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">Upcoming Reservations</h3>
          <button onClick={() => navigate("/hub/calendar")} className="text-xs text-forest hover:text-terracotta flex items-center gap-1 transition-colors" data-testid="view-calendar-link">
            Open Calendar <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>
        {data.upcoming.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm border border-dashed border-stone-200 m-5">No upcoming reservations. Reserve a zone from the Calendar.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-border">
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Group</th>
                <th className="px-5 py-3 font-semibold">Zone</th>
                <th className="px-5 py-3 font-semibold text-right">Pax</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-stone-50 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs">{r.date}</td>
                  <td className="px-5 py-3 font-medium">{r.group_name}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: r.zone_color }} />
                      {r.zone_name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono">{r.pax}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
